// src/features/admin/smtpSettingsApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Types based on your API response
export interface SmtpSettings {
  id: number;
  host: string;
  port: number;
  user: string;
  pass: string;
  from_name: string;
  from_email: string;
  support_email: string;
  contact: string;
  secure: string;
  created_at: string;
  updated_at?: string;
}

export interface SmtpSettingsApiResponse {
  success: boolean;
  message: string;
  data?: SmtpSettings;
}

export interface UpdateSmtpSettingsRequest {
  id: number;
  host: string;
  port: number;
  user: string;
  pass: string;
  from_name: string;
  from_email: string;
  support_email: string;
  contact: string;
  secure: string;
  created_at?: string;
}

export const SMTP_SETTINGS_API_CONFIG = {
  BASE_URL: process.env.REACT_APP_SERVER_ENDPOINT || 'https://fftestservice.sarsetech.com',
  AUTH_KEY: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
  ENDPOINTS: {
    SMTP_LIST: '/globalsettings/smtp-list',
    SMTP_UPDATE: '/globalsettings/smtp-update',
  },
};

export const smtpSettingsApi = createApi({
  reducerPath: 'smtpSettingsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: SMTP_SETTINGS_API_CONFIG.BASE_URL,
    prepareHeaders: (headers) => {
      headers.set('Content-Type', 'application/json');
      headers.set('authkey', SMTP_SETTINGS_API_CONFIG.AUTH_KEY);
      
      const token = localStorage.getItem('auth_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      
      return headers;
    },
  }),
  tagTypes: ['SmtpSettings'],
  endpoints: (build) => ({
    // Get SMTP Settings List
    getSmtpSettings: build.query<SmtpSettingsApiResponse, void>({
      query: () => ({
        url: SMTP_SETTINGS_API_CONFIG.ENDPOINTS.SMTP_LIST,
        method: 'POST',
        body: JSON.stringify({ id: 1 }),
      }),
      providesTags: ['SmtpSettings'],
      transformResponse: (response: any) => {
        console.log('SMTP Settings List Response:', response);
        return {
          success: response.success || false,
          message: response.message || '',
          data: response.data,
        };
      },
    }),

    // Update SMTP Settings
    updateSmtpSettings: build.mutation<SmtpSettingsApiResponse, UpdateSmtpSettingsRequest>({
      query: (data) => {
        console.log('Update SMTP Settings Data:', data);
        
        // Get current timestamp in ISO format for created_at
        const currentTimestamp = new Date().toISOString().replace('T', ' ').substring(0, 19);
        
        return {
          url: SMTP_SETTINGS_API_CONFIG.ENDPOINTS.SMTP_UPDATE,
          method: 'POST',
          body: JSON.stringify({
            id: data.id,
            host: data.host.trim(),
            port: data.port,
            user: data.user.trim(),
            pass: data.pass,
            from_name: data.from_name.trim(),
            from_email: data.from_email.trim(),
            support_email: data.support_email.trim(),
            contact: data.contact.trim(),
            secure: data.secure,
            created_at: data.created_at || currentTimestamp,
          }),
        };
      },
      invalidatesTags: ['SmtpSettings'],
      transformResponse: (response: any) => {
        console.log('Update SMTP Settings Response:', response);
        return {
          success: response.success || false,
          message: response.message || '',
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('Update SMTP Settings Error:', response);
        return {
          status: response.status,
          data: response.data || { message: 'Unknown error occurred' }
        };
      },
    }),
  }),
});

export const {
  useGetSmtpSettingsQuery,
  useUpdateSmtpSettingsMutation,
} = smtpSettingsApi;